/**
 * Ark Vault Shopify theme — UI + Cart Ajax API
 * Ported from the static prototype (js/common.js, js/cart-store.js, js/layout.js)
 */

const ThemeCart = {
  async fetchCart() {
    const res = await fetch('/cart.js');
    return res.json();
  },

  async add(variantId, quantity = 1) {
    const res = await fetch(window.theme.cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
    });
    if (!res.ok) throw new Error('Add to cart failed');
    return res.json();
  },

  async change(line, quantity) {
    const res = await fetch(window.theme.cartChangeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    return res.json();
  },

  updateBadge(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  },

  async refreshBadge() {
    const cart = await this.fetchCart();
    this.updateBadge(cart.item_count);
    return cart;
  },
};

function flashButton(btn, text = 'Added ✓') {
  if (!btn) return;
  const original = btn.textContent;
  btn.textContent = text;
  btn.disabled = true;
  setTimeout(() => {
    btn.textContent = original;
    btn.disabled = false;
  }, 1800);
}

function initHeader() {
  const menuToggle = document.querySelector('[data-menu-toggle]');
  const navMobile = document.querySelector('[data-mobile-nav]');
  const header = document.getElementById('header');

  window.addEventListener('scroll', () => {
    header?.classList.toggle('scrolled', window.scrollY > 8);
  }, { passive: true });

  menuToggle?.addEventListener('click', () => {
    const expanded = menuToggle.getAttribute('aria-expanded') === 'true';
    menuToggle.setAttribute('aria-expanded', String(!expanded));
    if (navMobile) navMobile.hidden = expanded;
  });

  navMobile?.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', () => {
      menuToggle?.setAttribute('aria-expanded', 'false');
      if (navMobile) navMobile.hidden = true;
    });
  });

  document.querySelector('[data-bottom-search]')?.addEventListener('click', () => {
    const input = document.querySelector('[data-search-input]');
    input?.focus();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
}

function initScrollReveal() {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('revealed');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.08 });

  document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
}

function initQuickAdd() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-add-to-cart]');
    if (!btn) return;
    e.preventDefault();
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    try {
      await ThemeCart.add(Number(variantId), 1);
      await ThemeCart.refreshBadge();
      flashButton(btn);
    } catch (err) {
      console.error(err);
    }
  });
}

function initGallery() {
  const thumbs = document.getElementById('gallery-thumbs');
  const main = document.getElementById('gallery-main-img');
  if (!thumbs || !main) return;
  thumbs.addEventListener('click', (e) => {
    const btn = e.target.closest('.gallery-thumb');
    if (!btn) return;
    main.src = btn.dataset.imageUrl;
    thumbs.querySelectorAll('.gallery-thumb').forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
  });
}

function initProductForm() {
  const form = document.getElementById('product-form');
  const productJson = document.getElementById('product-json');
  if (!form || !productJson) return;

  const product = JSON.parse(productJson.textContent);
  const variantInput = form.querySelector('[data-variant-id]');
  const priceBlock = document.getElementById('product-price');
  const addBtn = document.getElementById('add-to-cart');
  const qty = document.getElementById('quantity');

  function formatMoney(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  }

  function findVariant() {
    const selected = [...form.querySelectorAll('.variant-pills')].map((group) => {
      return group.querySelector('.variant-pill.active')?.dataset.value;
    });
    return product.variants.find((v) => {
      const opts = [v.option1, v.option2, v.option3].filter(Boolean);
      return opts.every((o, i) => !selected[i] || selected[i] === o);
    });
  }

  function updateVariant() {
    const variant = findVariant() || product.variants[0];
    if (!variant) return;
    variantInput.value = variant.id;
    const price = formatMoney(variant.price);
    const compare = variant.compare_at_price > variant.price
      ? `<span class="price-was">${formatMoney(variant.compare_at_price)}</span>` : '';
    priceBlock.innerHTML = `<span class="price-current"><span class="price-sale">${price}</span>${compare}</span>`;
    if (variant.available) {
      addBtn.disabled = false;
      addBtn.textContent = 'Add to Cart';
    } else {
      addBtn.disabled = true;
      addBtn.textContent = 'Sold out';
    }
  }

  form.querySelectorAll('.variant-pill').forEach((pill) => {
    pill.addEventListener('click', () => {
      const group = pill.closest('.variant-pills');
      group.querySelectorAll('.variant-pill').forEach((p) => p.classList.remove('active'));
      pill.classList.add('active');
      updateVariant();
    });
  });

  form.querySelector('[data-qty-minus]')?.addEventListener('click', () => {
    qty.value = Math.max(1, Number(qty.value) - 1);
  });
  form.querySelector('[data-qty-plus]')?.addEventListener('click', () => {
    qty.value = Math.min(99, Number(qty.value) + 1);
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    try {
      await ThemeCart.add(Number(variantInput.value), Number(qty.value));
      await ThemeCart.refreshBadge();
      flashButton(addBtn);
    } catch (err) {
      console.error(err);
    }
  });

  document.querySelectorAll('.tab-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.tab-btn').forEach((b) => b.classList.remove('active'));
      document.querySelectorAll('.tab-panel').forEach((p) => { p.classList.remove('active'); p.hidden = true; });
      btn.classList.add('active');
      const panel = document.getElementById(`tab-${btn.dataset.tab}`);
      panel?.classList.add('active');
      if (panel) panel.hidden = false;
    });
  });

  updateVariant();
}

function initCartPage() {
  // Event delegation so every line item qty button works (not only the first).
  document.addEventListener('click', async (e) => {
    const minus = e.target.closest('[data-cart-qty-minus]');
    const plus = e.target.closest('[data-cart-qty-plus]');
    if (!minus && !plus) return;

    const btn = minus || plus;
    const line = Number(btn.dataset.line);
    if (!line) return;

    const row = btn.closest('.cart-item');
    const display = row?.querySelector('.qty-display');
    if (!display) return;

    e.preventDefault();
    const current = Number(display.textContent) || 1;
    const qty = minus ? Math.max(0, current - 1) : current + 1;

    try {
      await ThemeCart.change(line, qty);
      window.location.reload();
    } catch (err) {
      console.error(err);
    }
  });

  // Checkout is an <a href=".../checkout"> — no JS intercept needed.
}

function initFilters() {
  const toggle = document.querySelector('[data-filter-toggle]');
  const panel = document.querySelector('[data-filters-panel]');
  const overlay = document.querySelector('[data-filter-overlay]');
  toggle?.addEventListener('click', () => {
    panel?.classList.toggle('open');
    if (overlay) overlay.hidden = !panel?.classList.contains('open');
  });
  overlay?.addEventListener('click', () => {
    panel?.classList.remove('open');
    overlay.hidden = true;
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  initScrollReveal();
  initQuickAdd();
  initGallery();
  initProductForm();
  initCartPage();
  initFilters();
  ThemeCart.refreshBadge();
});